/**
 * Navigation state shared by the views: where we are, the two badges, and
 * the unsaved-changes guard.
 *
 * The guard is core's `admin-shell.canLeave()`, which was written, tested and
 * never called by the prototype: the janazah composer and the timetable
 * review are long forms used under time pressure, and a click on the menu
 * threw either away without a word.
 */
import React, { createContext, useContext } from 'react';

export interface NavCtx {
  view: string;
  go: (id: string) => void;
  badges: Record<string, number>;
  refreshBadges: () => void;
  setDirty: (key: string, dirty: boolean) => void;
  today: string | null;
  /** What the mosque HAS (GET /mosque `capabilities`), or null while it is being asked. */
  caps: string[] | null;
  /**
   * Ask /mosque again, past every cache (PM gaps B1, 2026-11-01): after a switch on «Funksjoner» the menu follows at once
   * instead of five minutes later. Resolves to the new list (null when it could not be asked).
   */
  refreshCaps: () => Promise<string[] | null>;
}
export const Nav = createContext<NavCtx>({
  view: 'dash', go: () => {}, badges: {}, refreshBadges: () => {}, setDirty: () => {}, today: null, caps: null, refreshCaps: async () => null,
});
export const useNav = () => useContext(Nav);

/** Mark a form dirty while `dirty` is true; clean when the component leaves. */
export function useDirty(key: string, dirty: boolean) {
  const { setDirty } = useNav();
  React.useEffect(() => { setDirty(key, dirty); return () => setDirty(key, false); }, [key, dirty, setDirty]);
}
